import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { toast } from 'sonner';

import { api, type ApiResponse } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { VerificationOverlay } from '@/shared/components/VerificationOverlay';
import { PhoneCollectionOverlay } from '@/shared/components/PhoneCollectionOverlay';
import { DeviceAuthOverlay } from '@/shared/components/DeviceAuthOverlay';
import { getOrCreateDeviceId } from '@/lib/deviceId';

interface VerificationContextValue {
  requiresVerification: boolean;
}

const VerificationContext = createContext<VerificationContextValue>({ requiresVerification: false });

export function useVerification() {
  return useContext(VerificationContext);
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const visible = local.slice(0, 1);
  return `${visible}***@${domain}`;
}

function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return phone;
  const last4 = digits.slice(-4);
  const masked = '*'.repeat(Math.max(0, digits.length - 4));
  return `${masked}${last4}`;
}

type Stage = 'email' | 'phone-collect' | 'phone-verify' | 'device-auth' | 'none';

interface DeviceEntry {
  deviceId: string;
  authorizedAt?: string;
  authChannel?: 'email' | 'whatsapp';
}

export function VerificationProvider({ children }: { children: ReactNode }) {
  const { state, refreshAuth, logout } = useAuth();
  const { app, refreshApp } = useApp();
  const user = state.user;

  const [emailVerifiedLocal, setEmailVerifiedLocal] = useState(false);
  const [phoneVerifiedLocal, setPhoneVerifiedLocal] = useState(false);
  const [deviceAuthorizedLocal, setDeviceAuthorizedLocal] = useState(false);
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  // Device auth state
  const [currentDevice, setCurrentDevice] = useState<DeviceEntry | null>(null);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const deviceId = getOrCreateDeviceId();

  // DDI padrão da company — usado pelo PhoneCollectionOverlay pra validar o
  // telefone em tempo real (fallback 'BR' até a rota responder).
  const [defaultCountryCode, setDefaultCountryCode] = useState('BR');

  // Reseta flags locais quando o user muda (logout/login)
  useEffect(() => {
    setEmailVerifiedLocal(false);
    setPhoneVerifiedLocal(false);
    setDeviceAuthorizedLocal(false);
    setCurrentDevice(null);
    setError('');
  }, [user?.id]);

  // Busca device atual do usuário para decidir se precisa de 2FA
  useEffect(() => {
    if (!user?.id) return;
    if (state.masterLogin === true) return; // master session pula 2FA
    if (user.emailVerified === false && app?.hasEmailProvider) return; // espera email primeiro
    if (!app) return;

    let cancelled = false;
    setIsLoadingDevices(true);
    api.get('users/me/devices')
      .then((res) => {
        if (cancelled) return;
        const devices = (res.data?.data ?? []) as DeviceEntry[];
        const current = devices.find((d) => d.deviceId === deviceId) ?? null;
        setCurrentDevice(current);
      })
      .catch(() => { /* silent */ })
      .finally(() => {
        if (!cancelled) setIsLoadingDevices(false);
      });

    return () => { cancelled = true; };
  }, [user?.id, user?.emailVerified, app, deviceId, state.masterLogin]);

  // ============ Gates ============
  // Master login session (acesso de suporte via MASTER_PASSWORD) pula TODAS as
  // etapas de verificação. Ver backend: src/middlewares/requireVerifiedUser.ts
  const isMasterSession = state.masterLogin === true;

  const needsEmail =
    !isMasterSession &&
    !!user && !!app &&
    app.hasEmailProvider &&
    user.emailVerified === false &&
    !emailVerifiedLocal;

  const needsPhoneCollection =
    !isMasterSession &&
    !!user && !!app &&
    app.hasWhatsAppProvider &&
    !user.phone &&
    !needsEmail;

  const needsPhoneVerify =
    !isMasterSession &&
    !!user && !!app &&
    app.hasWhatsAppProvider &&
    !!user.phone &&
    user.phoneVerified === false &&
    !phoneVerifiedLocal &&
    !needsEmail &&
    !needsPhoneCollection;

  // Device 2FA — só dispara se:
  //   1. tem pelo menos um provider (email OU whatsapp+phoneVerified)
  //   2. device atual existe e não tem authorizedAt
  //   3. já passou pelos gates anteriores
  const hasAnyChannel =
    !!app && (app.hasEmailProvider || (app.hasWhatsAppProvider && user?.phoneVerified === true));

  const needsDeviceAuth =
    !isMasterSession &&
    !!user && !!app && !!currentDevice &&
    hasAnyChannel &&
    !currentDevice.authorizedAt &&
    !deviceAuthorizedLocal &&
    !needsEmail && !needsPhoneCollection && !needsPhoneVerify;

  const stage: Stage = needsEmail
    ? 'email'
    : needsPhoneCollection
      ? 'phone-collect'
      : needsPhoneVerify
        ? 'phone-verify'
        : needsDeviceAuth
          ? 'device-auth'
          : 'none';

  const requiresVerification = stage !== 'none';

  // Busca o DDI padrão da company só quando o modal de coleta de telefone
  // vai aparecer — evita chamada desnecessária nos demais estágios.
  useEffect(() => {
    if (stage !== 'phone-collect') return;
    let cancelled = false;
    api.get<ApiResponse<{ defaultCountryCode: string }>>('companies/me/default-country-code')
      .then((res) => {
        if (cancelled) return;
        const code = res.data?.data?.defaultCountryCode;
        if (code) setDefaultCountryCode(code);
      })
      .catch(() => { /* mantém fallback 'BR' */ });
    return () => { cancelled = true; };
  }, [stage]);

  // Re-lê GET /apps/me depois de uma falha de envio de código.
  //
  // Por que isso é necessário: `hasWhatsAppProvider` é derivado do circuit
  // breaker dos remetentes de 2FA no backend, e o PRIMEIRO usuário de um apagão
  // é justamente quem ABRE o breaker — ele carregou a página com
  // `hasWhatsAppProvider: true` (o breaker ainda estava fechado), caiu no gate,
  // pediu o código, o envio falhou e só ENTÃO o breaker abriu. Sem este refresh,
  // o `app` em memória continua dizendo `true`, o gate (overlay sem dismiss)
  // não sai e só um F5 destrava — a auto-cura funcionaria para todo mundo menos
  // para quem está de fato preso.
  //
  // Best-effort: uma falha do próprio refresh não pode virar erro na UI (o
  // usuário já está vendo o erro do envio).
  const refreshAppFlags = useCallback(async () => {
    try {
      await refreshApp();
    } catch {
      /* silencioso — o gate continua como está até o próximo load */
    }
  }, [refreshApp]);

  // ============ Email handlers ============
  const handleVerifyEmail = useCallback(async (code: string) => {
    setError('');
    setIsBusy(true);
    try {
      await api.post('users/verify-email', { code });
      setEmailVerifiedLocal(true);
      toast.success('Email verificado com sucesso!');
      refreshAuth().catch(() => { /* sync no background */ });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      setError(axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Erro ao verificar código');
    } finally {
      setIsBusy(false);
    }
  }, [refreshAuth]);

  const handleResendEmail = useCallback(async () => {
    setError('');
    try {
      await api.post('users/resend-verification');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      setError(axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Erro ao reenviar código');
    }
  }, []);

  // ============ Phone handlers ============
  const handleSubmitPhone = useCallback(async (phone: string) => {
    setError('');
    setIsBusy(true);
    try {
      await api.put('users/me/phone', { phone });
      await api.post('users/send-phone-verification');
      toast.success('Código enviado via WhatsApp');
      await refreshAuth();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      setError(axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Erro ao salvar telefone');
      // O envio pode ter aberto o breaker no backend — re-avalia o gate.
      await refreshAppFlags();
    } finally {
      setIsBusy(false);
    }
  }, [refreshAuth, refreshAppFlags]);

  const handleVerifyPhone = useCallback(async (code: string) => {
    setError('');
    setIsBusy(true);
    try {
      await api.post('users/verify-phone', { code });
      setPhoneVerifiedLocal(true);
      toast.success('Telefone verificado com sucesso!');
      refreshAuth().catch(() => { /* sync */ });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      setError(axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Erro ao verificar código');
    } finally {
      setIsBusy(false);
    }
  }, [refreshAuth]);

  const handleResendPhone = useCallback(async () => {
    setError('');
    try {
      await api.post('users/resend-phone-verification');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      setError(axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Erro ao reenviar código');
      // O envio pode ter aberto o breaker no backend — re-avalia o gate.
      await refreshAppFlags();
    }
  }, [refreshAppFlags]);

  // Auto-send phone code when entering verify stage
  const [phoneCodeSentFor, setPhoneCodeSentFor] = useState<string | null>(null);
  useEffect(() => {
    if (stage !== 'phone-verify' || !user?.id) return;
    if (phoneCodeSentFor === user.id) return;
    setPhoneCodeSentFor(user.id);
    api.post('users/send-phone-verification').catch((err: unknown) => {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      if (axiosErr?.response?.data?.message && !axiosErr.response.data.message.includes('Aguarde')) {
        setError(axiosErr.response.data.message);
      }
    });
  }, [stage, user?.id, phoneCodeSentFor]);

  // ============ Device auth handlers ============
  const handleSelectDeviceChannel = useCallback(async (channel: 'email' | 'whatsapp') => {
    setError('');
    setIsBusy(true);
    try {
      await api.post(`users/me/devices/${deviceId}/send-auth-code`, { channel });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      setError(axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Erro ao enviar código');
      throw err; // propaga para o overlay resetar seleção
    } finally {
      setIsBusy(false);
    }
  }, [deviceId]);

  const handleVerifyDeviceAuth = useCallback(async (code: string) => {
    setError('');
    setIsBusy(true);
    try {
      await api.post(`users/me/devices/${deviceId}/verify-auth`, { code });
      setDeviceAuthorizedLocal(true);
      toast.success('Dispositivo autorizado!');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      setError(axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Erro ao verificar código');
    } finally {
      setIsBusy(false);
    }
  }, [deviceId]);

  const handleResendDeviceAuth = useCallback(async () => {
    setError('');
    if (currentDevice?.authChannel) {
      try {
        await api.post(`users/me/devices/${deviceId}/send-auth-code`, { channel: currentDevice.authChannel });
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
        setError(axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Erro ao reenviar código');
      }
    }
  }, [currentDevice, deviceId]);

  if (isLoadingDevices && user && !needsEmail && !needsPhoneCollection && !needsPhoneVerify) {
    // Evita flash do DeviceAuthOverlay antes de saber o estado real
    return <VerificationContext.Provider value={{ requiresVerification: true }}>{children}</VerificationContext.Provider>;
  }

  return (
    <VerificationContext.Provider value={{ requiresVerification }}>
      {children}

      {stage === 'email' && user?.email && (
        <VerificationOverlay
          type="email"
          maskedTarget={maskEmail(user.email)}
          onVerify={handleVerifyEmail}
          onResend={handleResendEmail}
          error={error}
          isVerifying={isBusy}
          onLogout={logout}
        />
      )}

      {stage === 'phone-collect' && (
        <PhoneCollectionOverlay
          currentPhone={user?.phone}
          defaultCountry={defaultCountryCode}
          onSubmit={handleSubmitPhone}
          error={error}
          isSaving={isBusy}
          onLogout={logout}
        />
      )}

      {stage === 'phone-verify' && user?.phone && (
        <VerificationOverlay
          type="phone"
          maskedTarget={maskPhone(user.phone)}
          onVerify={handleVerifyPhone}
          onResend={handleResendPhone}
          error={error}
          isVerifying={isBusy}
          onLogout={logout}
        />
      )}

      {stage === 'device-auth' && user && app && (
        <DeviceAuthOverlay
          channelsAvailable={{
            email: !!app.hasEmailProvider && !!user.email,
            whatsapp: !!app.hasWhatsAppProvider && user.phoneVerified === true && !!user.phone,
          }}
          maskedEmail={user.email ? maskEmail(user.email) : undefined}
          maskedPhone={user.phone ? maskPhone(user.phone) : undefined}
          onSelectChannel={handleSelectDeviceChannel}
          onVerify={handleVerifyDeviceAuth}
          onResend={handleResendDeviceAuth}
          error={error}
          isBusy={isBusy}
          onLogout={logout}
        />
      )}
    </VerificationContext.Provider>
  );
}
